import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldAlert, KeyRound, Copy, Save, Users, Download, CircleDollarSign, Trophy, Pin, GripVertical } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

function AdminGate() {
  const { login, login2fa, user, logout } = useAuth();
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [busy, setBusy] = useState(false);
  const [preToken, setPreToken] = useState(null);
  const [code, setCode] = useState("");

  const submit = async () => {
    if (!u.trim() || !p) return toast.error("请输入管理员账号与密码");
    setBusy(true);
    try {
      const res = await login(u.trim(), p);
      if (res.requires_2fa) {
        setPreToken(res.pre_token);
        toast.success("账号密码正确，请输入谷歌验证码");
      } else if (res.role !== "admin") {
        toast.error("该账号不是管理员，已切换为会员身份");
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submit2fa = async () => {
    if (!code.trim()) return toast.error("请输入谷歌验证码");
    setBusy(true);
    try {
      await login2fa(preToken, code.trim());
      toast.success("谷歌验证通过，欢迎回来");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen grid place-items-center px-5" data-testid="admin-gate">
      <div className="panel p-10 w-full max-w-sm text-center">
        <ShieldAlert className="w-10 h-10 text-[#D4AF37] mx-auto mb-6" />
        <h1 className="font-serif-sc text-2xl font-bold mb-2 tracking-widest">管理后台</h1>
        <p className="text-xs text-gray-500 mb-8">独立管理入口 · 与会员前台完全分离</p>
        {user && user.role !== "admin" && !preToken && (
          <p className="text-xs text-yellow-400/90 mb-4">当前登录的是会员账号 {user.username}，请使用管理员账号登录</p>
        )}
        {preToken ? (
          <>
            <p className="text-xs text-emerald-400 mb-4">账号密码校验通过 · 谷歌验证已开启</p>
            <input
              data-testid="admin-gate-2fa-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => e.key === "Enter" && submit2fa()}
              placeholder="请输入 6 位谷歌验证码"
              inputMode="numeric"
              className="input-gold w-full px-4 py-3 text-center text-lg font-mono-num tracking-[0.5em] mb-5"
            />
            <button data-testid="admin-gate-2fa-btn" onClick={submit2fa} disabled={busy} className="btn-gold w-full py-3 text-sm">
              {busy ? "验证中..." : "验证并登录"}
            </button>
            <button
              data-testid="admin-gate-back-btn"
              onClick={() => { setPreToken(null); setCode(""); }}
              className="mt-4 text-xs text-gray-600 hover:text-gray-400 transition-colors"
            >
              返回重新输入账号密码
            </button>
          </>
        ) : (
          <>
            <input
              data-testid="admin-gate-username"
              value={u}
              onChange={(e) => setU(e.target.value)}
              placeholder="管理员账号"
              className="input-gold w-full px-4 py-3 text-sm mb-3"
            />
            <input
              data-testid="admin-gate-password"
              type="password"
              value={p}
              onChange={(e) => setP(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="管理员密码"
              className="input-gold w-full px-4 py-3 text-sm mb-5"
            />
            <button data-testid="admin-gate-login-btn" onClick={submit} disabled={busy} className="btn-gold w-full py-3 text-sm">
              {busy ? "登录中..." : "登录管理后台"}
            </button>
            {user && user.role !== "admin" && (
              <button data-testid="admin-gate-logout-btn" onClick={logout} className="mt-4 text-xs text-gray-600 hover:text-gray-400 transition-colors">
                退出当前会员账号
              </button>
            )}
          </>
        )}
      </div>
    </main>
  );
}

export default function Admin() {
  const { user, ready } = useAuth();
  const [keys, setKeys] = useState([]);
  const [users, setUsers] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [smsBalance, setSmsBalance] = useState(null);
  const [overview, setOverview] = useState(null);
  const [memberQuery, setMemberQuery] = useState("");
  const [memberLevel, setMemberLevel] = useState("all");
  const [verifyQuery, setVerifyQuery] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyLogs, setVerifyLogs] = useState([]);
  const [tab, setTab] = useState("overview");
  const [keyFilter, setKeyFilter] = useState("all");
  const [security, setSecurity] = useState(null);
  const [pwdForm, setPwdForm] = useState({ old_password: "", new_password: "" });
  const [faSetup, setFaSetup] = useState(null);
  const [faCode, setFaCode] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [notifyTitle, setNotifyTitle] = useState("");
  const [notifyContent, setNotifyContent] = useState("");
  const [resetPwd, setResetPwd] = useState("");
  const [projects, setProjects] = useState([]);
  const [projForm, setProjForm] = useState({ title: "", daily_income: "500+", summary: "", detail: "", tags: "", views: "" });
  const [submissions, setSubmissions] = useState([]);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveForm, setApproveForm] = useState({ title: "", daily_income: "500+", summary: "", detail: "", tags: "会员投稿,平台甄选", reward: 188888 });
  const [count, setCount] = useState(10);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const load = () => {
    api.get("/admin/keys").then((r) => setKeys(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/withdrawals").then((r) => setWithdrawals(r.data)).catch(() => {});
    api.get("/admin/bonuses").then((r) => setBonuses(r.data)).catch(() => {});
    api.get("/admin/sms/balance").then((r) => setSmsBalance(r.data)).catch(() => setSmsBalance(null));
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/admin/submissions").then((r) => setSubmissions(r.data)).catch(() => {});
    api.get("/admin/verify-logs").then((r) => setVerifyLogs(r.data)).catch(() => {});
    api.get("/admin/security").then((r) => setSecurity(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user?.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!ready) return <div className="min-h-screen" />;

  if (!user || user.role !== "admin") {
    return <AdminGate />;
  }

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/keys/generate", { count: Number(count) || 10 });
      toast.success(`已生成 ${data.generated.length} 张卡密`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyUnused = () => {
    const text = keys.filter((k) => k.status === "unused").map((k) => k.key).join("\n");
    navigator.clipboard.writeText(text).then(() => toast.success("已复制全部未使用卡密"));
  };

  const exportKeys = async () => {
    try {
      const res = await api.get("/admin/keys/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "heying_card_keys.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("已导出未使用卡密 CSV");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const exportMembers = async () => {
    try {
      const res = await api.get("/admin/members/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "heying_members.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("已导出会员 CSV");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const exportWithdrawals = async () => {
    try {
      const res = await api.get("/admin/withdrawals/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "heying_withdrawals.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("已导出提现记录 CSV（含手续费/实收/折合USDT）");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const actWithdraw = async (wid, action) => {
    try {
      await api.post(`/admin/withdrawals/${wid}/${action}`);
      toast.success(action === "approve" ? "已确认打款" : "已拒绝该申请，金额已退回会员余额");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const settleBonus = async (periodType) => {
    try {
      const { data } = await api.post("/admin/bonuses/settle", { period_type: periodType });
      toast.success(`已结算：${data.winner} 获得 ¥${data.amount}（${data.period_key}）`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openMember = async (username) => {
    setDetailOpen(true);
    setDetail(null);
    try {
      const { data } = await api.get(`/admin/members/${username}`);
      setDetail(data);
    } catch (e) {
      toast.error(apiError(e));
      setDetailOpen(false);
    }
  };

  const sendMemberNotify = async () => {
    if (!detail) return;
    if (!notifyContent.trim()) return toast.error("请输入通知内容");
    try {
      await api.post(`/admin/members/${detail.user.username}/notify`, {
        title: notifyTitle.trim() || "社群通知",
        content: notifyContent.trim(),
      });
      toast.success("通知已发送，会员端铃铛即时可见");
      setNotifyTitle("");
      setNotifyContent("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const resetMemberPwd = async () => {
    if (!detail) return;
    if (resetPwd.length < 6) return toast.error("新密码至少6位");
    try {
      await api.post(`/admin/members/${detail.user.username}/reset-password`, { new_password: resetPwd });
      toast.success(`已重置 ${detail.user.username} 的密码并通知本人`);
      setResetPwd("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const publishProject = async () => {
    try {
      await api.post("/admin/projects", {
        ...projForm,
        tags: projForm.tags.split(/[,，]/).map((t) => t.trim()).filter(Boolean),
        views: parseInt(projForm.views, 10) || 0,
      });
      toast.success("项目已发布，首页即时可见");
      setProjForm({ title: "", daily_income: "500+", summary: "", detail: "", tags: "", views: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleProject = async (pid) => {
    try {
      const { data } = await api.post(`/admin/projects/${pid}/toggle`);
      toast.success(data.status === "active" ? "项目已上架" : "项目已下架");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const pinProject = async (pid) => {    try {
      const { data } = await api.post(`/admin/projects/${pid}/pin`);
      toast.success(data.is_pinned ? "项目已置顶" : "已取消置顶");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const dropProject = async (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex) return;
    const next = [...projects];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    setDragIndex(null);
    setProjects(next);
    try {
      await api.post("/admin/projects/reorder", { pids: next.map((p) => p.pid) });
      toast.success("展示顺序已保存");
    } catch (e) {
      toast.error(apiError(e));
      load();
    }
  };

  const reviewSubmission = async (sid, action) => {
    try {
      await api.post(`/admin/submissions/${sid}/${action}`, { note: "" });
      toast.success("已拒绝并通知投稿人");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openApprove = (s) => {
    setApproveTarget(s);
    setApproveForm({
      title: s.title || "",
      daily_income: "500+",
      summary: s.summary || "",
      detail: s.detail || "",
      tags: "会员投稿,平台甄选",
      reward: 188888,
    });
  };

  const confirmApprove = async () => {
    if (!approveForm.title.trim() || !approveForm.summary.trim()) return toast.error("上架标题和简介不能为空");
    const reward = Math.max(1, Math.min(Number(approveForm.reward) || 188888, 188888));
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/submissions/${approveTarget.sid}/approve`, {
        title: approveForm.title,
        daily_income: approveForm.daily_income,
        summary: approveForm.summary,
        detail: approveForm.detail,
        tags: approveForm.tags.split(/[,，、]/).map((t) => t.trim()).filter(Boolean),
        reward,
      });
      toast.success(`已录用并自动上架「今日项目」，¥${(data.reward || reward).toLocaleString()} 奖励已发放并通知投稿人`);
      setApproveTarget(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", settings);
      toast.success("设置已保存");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const verifyMember = async () => {
    if (!verifyQuery.trim()) return toast.error("请输入合赢账户");
    try {
      const { data } = await api.get(`/admin/members/${encodeURIComponent(verifyQuery.trim())}/verify`);
      setVerifyResult(data);
      api.get("/admin/verify-logs").then((r) => setVerifyLogs(r.data)).catch(() => {});
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const changeAdminPwd = async () => {
    if (!pwdForm.old_password || !pwdForm.new_password) return toast.error("请填写原密码与新密码");
    try {
      await api.post("/auth/change-password", pwdForm);
      toast.success("管理员密码已修改，下次登录生效");
      setPwdForm({ old_password: "", new_password: "" });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setup2fa = async () => {
    try {
      const { data } = await api.post("/admin/2fa/setup");
      setFaSetup(data);
      setFaCode("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const enable2fa = async () => {
    if (!faCode.trim()) return toast.error("请输入谷歌验证码");
    try {
      await api.post("/admin/2fa/enable", { code: faCode.trim() });
      toast.success("谷歌验证已开启，下次登录需输入验证码");
      setFaSetup(null);
      setFaCode("");
      api.get("/admin/security").then((r) => setSecurity(r.data)).catch(() => {});
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const disable2fa = async () => {
    if (!faCode.trim()) return toast.error("请输入谷歌验证码");
    try {
      await api.post("/admin/2fa/disable", { code: faCode.trim() });
      toast.success("谷歌验证已关闭");
      setFaCode("");
      api.get("/admin/security").then((r) => setSecurity(r.data)).catch(() => {});
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteKey = async (key) => {
    try {
      await api.delete(`/admin/keys/${encodeURIComponent(key)}`);
      toast.success("卡密已作废删除");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const exportVerifyLogs = async () => {
    try {
      const res = await api.get("/admin/verify-logs/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "heying_verify_logs.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("已导出核验台账 CSV");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setVerdict = async (vid, verdict) => {
    try {
      await api.post(`/admin/verify-logs/${vid}/verdict`, { verdict });
      toast.success(verdict === "approved" ? "已记录：放行入群" : "已记录：拒绝入群");
      setVerifyResult((r) => (r && r.vid === vid ? { ...r, verdict } : r));
      api.get("/admin/verify-logs").then((r) => setVerifyLogs(r.data)).catch(() => {});
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <main className="min-h-screen pt-28 pb-24 px-5 lg:px-8" data-testid="admin-panel">
      <div className="mx-auto max-w-6xl">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="font-mono-num text-xs tracking-[0.35em] text-[#D4AF37] mb-4">ADMIN · 管理后台</p>
          <h1 className="font-serif-sc text-3xl sm:text-4xl font-bold tracking-tight mb-8">卡密与站点管理</h1>
        </motion.div>

        <div
          className="flex gap-2 overflow-x-auto pb-2 mb-10"
          data-lenis-prevent
          data-testid="admin-tabs"
          onWheel={(e) => {
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) e.currentTarget.scrollLeft += e.deltaY;
          }}
        >
          {[
            ["overview", "数据概览", overview ? null : null],
            ["members", "用户管理", null],
            ["finance", "财务管理", withdrawals.filter((w) => w.status === "pending").length || null],
            ["keys", "卡密管理", keys.filter((k) => k.status === "unused").length || null],
            ["projects", "项目与投稿", submissions.filter((s) => s.status === "pending").length || null],
            ["settings", "站点设置", null],
          ].map(([id, name, badge]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`px-5 py-2.5 text-xs tracking-widest whitespace-nowrap border transition-colors inline-flex items-center gap-2 ${
                tab === id ? "bg-[#D4AF37] text-[#070709] font-bold border-[#D4AF37]" : "border-[#D4AF37]/20 text-gray-400 hover:text-[#D4AF37] hover:border-[#D4AF37]/50"
              }`}
            >
              {name}
              {badge != null && badge > 0 && (
                <span className={`font-mono-num text-[10px] px-1.5 py-0.5 ${tab === id ? "bg-[#070709]/20" : "bg-[#D4AF37]/15 text-[#D4AF37]"}`}>{badge}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "overview" && overview && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#D4AF37]/10 border border-[#D4AF37]/10 mb-8" data-testid="admin-overview-cards">
            {[
              { label: "今日新增会员", value: overview.today_new_members, testid: "ov-today-members" },
              { label: "今日邀请数", value: overview.today_invites, testid: "ov-today-invites" },
              { label: "今日短信消耗", value: overview.today_sms, testid: "ov-today-sms" },
              { label: "待处理提现", value: overview.pending_withdrawals, testid: "ov-pending-withdrawals" },
              { label: "会员总数", value: overview.total_members, testid: "ov-total-members" },
              { label: "已激活会员", value: overview.activated_members, testid: "ov-activated-members" },
              { label: "未使用卡密", value: overview.unused_keys, testid: "ov-unused-keys" },
              { label: "短信剩余", value: smsBalance && !smsBalance.demo ? smsBalance.items : "—", testid: "ov-sms-left" },
            ].map((c) => (
              <div key={c.label} className="bg-[#0A0A0E] p-5">
                <p className="text-[11px] text-gray-500 tracking-widest mb-2">{c.label}</p>
                <p className="font-mono-num text-2xl text-[#F3E5AB]" data-testid={c.testid}>{c.value}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "keys" && (
        <div className="panel p-8 mb-8">
          {smsBalance && (
            <p className="text-xs text-gray-500 tracking-widest mb-4" data-testid="sms-balance-display">
              短信通道：{smsBalance.demo ? "演示模式（未配置密钥）" : `大狗短信已接入 · 剩余 ${smsBalance.items} 条 · 已用 ${smsBalance.use_items} 条`}
            </p>
          )}
          <div className="flex flex-wrap items-end gap-4 mb-6">
            <div>
              <p className="text-xs text-gray-500 tracking-widest mb-2">生成数量（1-100）</p>
              <input
                data-testid="key-count-input"
                type="number"
                min="1"
                max="100"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className="input-gold w-32 px-4 py-2.5 text-sm font-mono-num"
              />
            </div>
            <button data-testid="generate-card-keys-btn" onClick={generate} disabled={busy} className="btn-gold px-6 py-2.5 text-sm inline-flex items-center gap-2">
              <KeyRound className="w-4 h-4" /> {busy ? "生成中..." : "批量生成卡密"}
            </button>
            <button data-testid="copy-unused-keys-btn" onClick={copyUnused} className="btn-ghost-gold px-6 py-2.5 text-sm inline-flex items-center gap-2">
              <Copy className="w-4 h-4" /> 复制未使用卡密
            </button>
            <button data-testid="export-keys-btn" onClick={exportKeys} className="btn-ghost-gold px-6 py-2.5 text-sm inline-flex items-center gap-2">
              <Download className="w-4 h-4" /> 导出未使用卡密 CSV
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="key-filter-bar">
            {[["all", "全部"], ["unused", "未使用"], ["used", "已使用"]].map(([f, name]) => (
              <button
                key={f}
                data-testid={`key-filter-${f}`}
                onClick={() => setKeyFilter(f)}
                className={`px-4 py-1.5 text-xs tracking-widest border transition-colors ${
                  keyFilter === f ? "border-[#D4AF37]/60 text-[#D4AF37]" : "border-[#D4AF37]/15 text-gray-500 hover:text-gray-300"
                }`}
              >
                {name}（{f === "all" ? keys.length : keys.filter((k) => k.status === f).length}）
              </button>
            ))}
          </div>
          <div className="max-h-72 overflow-auto border border-[#D4AF37]/10" data-lenis-prevent data-testid="keys-table">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#0F0F14]">
                <tr className="text-left text-xs text-gray-500 tracking-widest">
                  <th className="px-4 py-3 font-normal">卡密</th>
                  <th className="px-4 py-3 font-normal">状态</th>
                  <th className="px-4 py-3 font-normal">使用者</th>
                  <th className="px-4 py-3 font-normal">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#D4AF37]/10">
                {keys.filter((k) => keyFilter === "all" || k.status === keyFilter).map((k) => (
                  <tr key={k.key}>
                    <td className="px-4 py-2.5 font-mono-num text-[#F3E5AB]">{k.key}</td>
                    <td className="px-4 py-2.5">
                      <span className={k.status === "unused" ? "text-emerald-400" : "text-gray-500"}>
                        {k.status === "unused" ? "未使用" : "已使用"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-400">{k.used_by || "—"}</td>
                    <td className="px-4 py-2.5">
                      {k.status === "unused" && (
                        <button
                          data-testid={`key-void-btn-${k.key}`}
                          onClick={() => deleteKey(k.key)}
                          className="text-xs text-red-400 border border-red-500/40 px-3 py-1 hover:bg-red-500/10 transition-colors"
                        >
                          作废
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {tab === "settings" && settings && (
          <div className="panel p-8 mb-8">
            <h3 className="font-serif-sc text-lg font-bold mb-6">站点设置</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {[
                ["video_url", "宣传视频链接（16:9）"],
                ["video_poster", "视频封面图链接（可空）"],
                ["taobao_url", "淘宝购卡链接"],
                ["vip_group_id", "VIP群 ID"],
                ["vip_group_link", "VIP群 链接"],
                ["svip_group_id", "超级VIP群 ID"],
                ["svip_group_link", "超级VIP群 链接"],
                ["normal_group_id", "普通会员海鸥群 ID"],
                ["normal_group_link", "普通会员海鸥群 链接"],
                ["chat_app_url", "海鸥APP 下载链接"],
                ["weekly_top_reward", "周榜榜首奖励（¥）"],
                ["monthly_top_reward", "月榜榜首奖励（¥）"],
                ["usdt_rate", "USDT 汇率（1 USDT = ¥?）"],
                ["withdraw_fee_rate", "提现手续费比例（%）"],
                ["monthly_submit_reward", "月度投稿王奖励（¥）"],
                ["weekly_submit_reward", "周榜投稿王奖励（¥）"],
                ["admin_ip_whitelist", "后台IP白名单（逗号分隔，留空不限制）"],
              ].map(([field, label]) => (
                <label key={field} className="block">
                  <span className="text-xs text-gray-500 tracking-widest block mb-2">{label}</span>
                  <input
                    data-testid={`setting-${field}`}
                    value={settings[field] || ""}
                    onChange={(e) => setSettings((s) => ({ ...s, [field]: e.target.value }))}
                    className="input-gold w-full px-4 py-2.5 text-sm font-mono-num"
                  />
                </label>
              ))}
            </div>
            <label className="flex items-center gap-3 mb-6 cursor-pointer select-none" data-testid="setting-fee-enabled-wrap">
              <input
                type="checkbox"
                data-testid="setting-withdraw_fee_enabled"
                checked={!!settings.withdraw_fee_enabled}
                onChange={(e) => setSettings((s) => ({ ...s, withdraw_fee_enabled: e.target.checked }))}
                className="w-4 h-4 accent-[#D4AF37]"
              />
              <span className="text-xs text-gray-400 tracking-widest">
                开启提现手续费（按上方比例从提现金额扣除，打款通知自动写明实收金额）
              </span>
            </label>
            <button data-testid="save-settings-btn" onClick={saveSettings} className="btn-gold px-6 py-2.5 text-sm inline-flex items-center gap-2">
              <Save className="w-4 h-4" /> 保存设置
            </button>

            <div className="mt-10 pt-8 border-t border-[#D4AF37]/15" data-testid="security-section">
              <h4 className="font-serif-sc text-lg font-bold mb-6 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-[#D4AF37]" /> 安全设置
              </h4>
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="border border-[#D4AF37]/15 bg-[#0A0A0E] p-6" data-testid="admin-pwd-card">
                  <p className="text-sm font-bold mb-1">修改管理员密码</p>
                  <p className="text-[11px] text-gray-600 mb-4">修改后下次登录生效，当前会话不受影响</p>
                  <input
                    data-testid="admin-pwd-old"
                    type="password"
                    value={pwdForm.old_password}
                    onChange={(e) => setPwdForm((f) => ({ ...f, old_password: e.target.value }))}
                    placeholder="原密码"
                    className="input-gold w-full px-4 py-2.5 text-sm mb-3"
                  />
                  <input
                    data-testid="admin-pwd-new"
                    type="password"
                    value={pwdForm.new_password}
                    onChange={(e) => setPwdForm((f) => ({ ...f, new_password: e.target.value }))}
                    placeholder="新密码（至少6位）"
                    className="input-gold w-full px-4 py-2.5 text-sm mb-4"
                  />
                  <button data-testid="admin-pwd-save-btn" onClick={changeAdminPwd} className="btn-gold w-full py-2.5 text-xs">
                    修改密码
                  </button>
                </div>
                <div className="border border-[#D4AF37]/15 bg-[#0A0A0E] p-6" data-testid="admin-ip-card">
                  <p className="text-sm font-bold mb-1">IP 白名单</p>
                  <p className="text-[11px] text-gray-600 mb-4">
                    您的当前IP：<span className="font-mono-num text-[#D4AF37]" data-testid="admin-my-ip">{security?.my_ip || "..."}</span>
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    在上方「后台IP白名单」字段填入允许访问后台的 IP（多个用英文逗号分隔）并保存。留空表示不限制。开启后非白名单 IP 调用任何管理接口都会被拒绝。
                  </p>
                  <p className="text-[11px] text-yellow-400/80 mt-3">注意：填入前请先确认包含您的当前IP，否则会把自己挡在门外</p>
                </div>
                <div className="border border-[#D4AF37]/15 bg-[#0A0A0E] p-6" data-testid="admin-2fa-card">
                  <p className="text-sm font-bold mb-1">谷歌验证（Google Authenticator）</p>
                  <p className="text-[11px] mb-4">
                    状态：
                    <span data-testid="admin-2fa-status" className={security?.totp_enabled ? "text-emerald-400" : "text-gray-500"}>
                      {security?.totp_enabled ? "已开启 · 登录需验证码" : "未开启"}
                    </span>
                  </p>
                  {!security?.totp_enabled && !faSetup && (
                    <button data-testid="admin-2fa-setup-btn" onClick={setup2fa} className="btn-ghost-gold w-full py-2.5 text-xs">
                      生成绑定二维码
                    </button>
                  )}
                  {faSetup && !security?.totp_enabled && (
                    <div>
                      <img src={`data:image/png;base64,${faSetup.qr_base64}`} alt="谷歌验证二维码" data-testid="admin-2fa-qr" className="w-36 h-36 mx-auto mb-3 border border-[#D4AF37]/25" />
                      <p className="text-[10px] text-gray-600 text-center font-mono-num break-all mb-3">无法扫码？手动输入密钥：{faSetup.secret}</p>
                      <input
                        data-testid="admin-2fa-enable-code"
                        value={faCode}
                        onChange={(e) => setFaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="输入 App 显示的 6 位验证码"
                        inputMode="numeric"
                        className="input-gold w-full px-4 py-2.5 text-sm text-center font-mono-num mb-3"
                      />
                      <button data-testid="admin-2fa-enable-btn" onClick={enable2fa} className="btn-gold w-full py-2.5 text-xs">
                        验证并开启
                      </button>
                    </div>
                  )}
                  {security?.totp_enabled && (
                    <div>
                      <input
                        data-testid="admin-2fa-disable-code"
                        value={faCode}
                        onChange={(e) => setFaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="输入当前 6 位验证码以关闭"
                        inputMode="numeric"
                        className="input-gold w-full px-4 py-2.5 text-sm text-center font-mono-num mb-3"
                      />
                      <button data-testid="admin-2fa-disable-btn" onClick={disable2fa} className="w-full py-2.5 text-xs text-red-400 border border-red-500/40 hover:bg-red-500/10 transition-colors">
                        关闭谷歌验证
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "projects" && (
        <>
        <div className="panel p-8 mb-8">
          <h3 className="font-serif-sc text-lg font-bold mb-2 flex items-center gap-2">
            <Save className="w-5 h-5 text-[#D4AF37]" /> 投稿审核（{submissions.filter((s) => s.status === "pending").length} 笔待审）
          </h3>
          <p className="text-xs text-gray-500 mb-6">平台审核评估 · 录用奖励最高 ¥188,888（金额审核时确定）并自动上架「今日项目」展示，审核到上架一步完成</p>
          {submissions.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="submissions-empty">暂无投稿</p>
          ) : (
            <div className="divide-y divide-[#D4AF37]/10 border border-[#D4AF37]/10 px-4" data-testid="submissions-table">
              {submissions.map((s, i) => (
                <div key={s.sid} className="py-4">
                  <div className="flex flex-wrap items-center gap-3 text-sm mb-2">
                    <span className="text-gray-100 font-bold">{s.title}</span>
                    <span className="font-mono-num text-xs text-gray-500">{s.username} · {s.contact}</span>
                    <span
                      className={`text-[10px] tracking-widest px-2 py-0.5 border ${
                        s.status === "pending"
                          ? "text-yellow-400 border-yellow-500/40"
                          : s.status === "approved"
                            ? "text-emerald-400 border-emerald-500/40"
                            : "text-red-400 border-red-500/40"
                      }`}
                    >
                      {s.status === "pending" ? "待审核" : s.status === "approved" ? "已录用" : "已拒绝"}
                    </span>
                    {s.status === "pending" && (
                      <span className="ml-auto flex gap-2">
                        <button
                          data-testid={`submission-approve-btn-${i}`}
                          onClick={() => openApprove(s)}
                          className="text-xs text-[#070709] bg-[#D4AF37] px-3 py-1.5 hover:bg-[#E5C158] transition-colors"
                        >
                          录用并上架
                        </button>
                        <button
                          data-testid={`submission-reject-btn-${i}`}
                          onClick={() => reviewSubmission(s.sid, "reject")}
                          className="text-xs text-red-400 border border-red-500/40 px-3 py-1.5 hover:bg-red-500/10 transition-colors"
                        >
                          拒绝
                        </button>
                      </span>
                    )}
                    {s.status === "approved" && s.published_pid && (
                      <span className="ml-auto text-[10px] tracking-widest text-[#D4AF37]/70 font-mono-num">已上架 · {s.published_pid}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mb-1">{s.summary}</p>
                  {s.detail && (
                    <details className="text-xs text-gray-500">
                      <summary className="cursor-pointer text-[#D4AF37]/70 hover:text-[#D4AF37]">查看项目详情</summary>
                      <p className="mt-2 whitespace-pre-wrap leading-relaxed">{s.detail}</p>
                    </details>
                  )}
                  {s.review_note && <p className="text-[10px] text-red-400/70 mt-1">拒绝原因:{s.review_note}</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel p-8 mb-8">
          <h3 className="font-serif-sc text-lg font-bold mb-2 flex items-center gap-2">
            <CircleDollarSign className="w-5 h-5 text-[#D4AF37]" /> 今日项目管理
          </h3>
          <p className="text-xs text-gray-500 mb-6">发布后首页「今日项目」即时展示；完整拆解内容仅激活会员可见，访客端模糊锁定；拖动行调整展示顺序，置顶项目排在最前</p>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <input
              data-testid="proj-title-input"
              value={projForm.title}
              onChange={(e) => setProjForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="项目标题，如：闲鱼无货源矩阵"
              className="input-gold px-4 py-2.5 text-sm"
            />
            <input
              data-testid="proj-income-input"
              value={projForm.daily_income}
              onChange={(e) => setProjForm((f) => ({ ...f, daily_income: e.target.value }))}
              placeholder="日赚额度，如：500+ / 300-800"
              className="input-gold px-4 py-2.5 text-sm font-mono-num"
            />
            <input
              data-testid="proj-tags-input"
              value={projForm.tags}
              onChange={(e) => setProjForm((f) => ({ ...f, tags: e.target.value }))}
              placeholder="标签（逗号分隔，如：零成本,新手友好）"
              className="input-gold px-4 py-2.5 text-sm"
            />
            <input
              data-testid="proj-views-input"
              value={projForm.views}
              onChange={(e) => setProjForm((f) => ({ ...f, views: e.target.value.replace(/[^0-9]/g, "") }))}
              placeholder="初始围观人数（留空随机 200-1000）"
              className="input-gold px-4 py-2.5 text-sm font-mono-num"
            />
            <textarea
              data-testid="proj-summary-input"
              value={projForm.summary}
              onChange={(e) => setProjForm((f) => ({ ...f, summary: e.target.value }))}
              placeholder="公开简介（访客可见）"
              rows={2}
              className="input-gold px-4 py-2.5 text-sm md:col-span-2 resize-none"
            />
            <textarea
              data-testid="proj-detail-input"
              value={projForm.detail}
              onChange={(e) => setProjForm((f) => ({ ...f, detail: e.target.value }))}
              placeholder="完整拆解内容（仅激活会员可见，访客端模糊锁定）"
              rows={5}
              className="input-gold px-4 py-2.5 text-sm md:col-span-2"
            />
          </div>
          <button data-testid="proj-publish-btn" onClick={publishProject} className="btn-gold px-6 py-2.5 text-sm mb-6">
            发布项目
          </button>
          {projects.length > 0 && (
            <div className="divide-y divide-[#D4AF37]/10 border border-[#D4AF37]/10 px-4" data-testid="projects-table">
              {projects.map((p, i) => (
                <div
                  key={p.pid}
                  draggable
                  onDragStart={() => setDragIndex(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => dropProject(i)}
                  onDragEnd={() => setDragIndex(null)}
                  data-testid={`project-row-${i}`}
                  className={`py-3 flex items-center gap-3 text-sm cursor-grab active:cursor-grabbing transition-colors ${
                    dragIndex === i ? "opacity-40" : ""
                  } ${p.is_pinned ? "bg-[#D4AF37]/5 -mx-2 px-2" : ""}`}
                >
                  <GripVertical className="w-4 h-4 text-gray-600 shrink-0" />
                  <span className="font-mono-num text-xs text-[#D4AF37] shrink-0">日赚{p.daily_income}</span>
                  <span className="text-gray-200 flex-1 truncate">{p.title}</span>
                  <span className="font-mono-num text-[10px] text-gray-600 shrink-0" data-testid={`project-row-views-${i}`}>{(p.views || 0).toLocaleString()} 围观</span>
                  {p.is_pinned && (
                    <span className="text-[10px] tracking-widest px-2 py-0.5 border border-[#D4AF37]/50 text-[#D4AF37] shrink-0" data-testid={`project-pinned-badge-${i}`}>置顶</span>
                  )}
                  <span className={`text-[10px] tracking-widest px-2 py-0.5 border shrink-0 ${
                    p.status === "active" ? "text-emerald-400 border-emerald-500/40" : "text-gray-500 border-gray-700"
                  }`}>
                    {p.status === "active" ? "展示中" : "已下架"}
                  </span>
                  <button
                    data-testid={`proj-pin-btn-${i}`}
                    onClick={() => pinProject(p.pid)}
                    title={p.is_pinned ? "取消置顶" : "置顶"}
                    className={`p-1.5 border transition-colors shrink-0 ${
                      p.is_pinned ? "border-[#D4AF37]/60 text-[#D4AF37]" : "border-[#D4AF37]/15 text-gray-500 hover:text-[#D4AF37]"
                    }`}
                  >
                    <Pin className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid={`proj-toggle-btn-${i}`}
                    onClick={() => toggleProject(p.pid)}
                    className="btn-ghost-gold px-3 py-1.5 text-xs shrink-0"
                  >
                    {p.status === "active" ? "下架" : "上架"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        </>
        )}

        {tab === "finance" && (
        <>
        <div className="panel p-8 mb-8">
          <h3 className="font-serif-sc text-lg font-bold mb-2 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#D4AF37]" /> 榜首奖励结算
          </h3>
          <p className="text-xs text-gray-500 mb-6">周期结束后系统自动结算发放并通知本人；如系统未触发，可在此手动补结算（同一周期不会重复发放）</p>
          <div className="flex flex-wrap gap-3 mb-6">
            <button data-testid="settle-week-btn" onClick={() => settleBonus("week")} className="btn-gold px-6 py-2.5 text-sm">
              结算上周榜首
            </button>
            <button data-testid="settle-month-btn" onClick={() => settleBonus("month")} className="btn-gold px-6 py-2.5 text-sm">
              结算上月榜首
            </button>
          </div>
          {bonuses.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="bonuses-empty">暂无结算记录</p>
          ) : (
            <div className="divide-y divide-[#D4AF37]/10 border border-[#D4AF37]/10 px-4" data-testid="bonuses-list">
              {bonuses.map((b, i) => (
                <div key={i} className="py-3 flex flex-wrap items-center gap-x-4 text-sm">
                  <span className="font-mono-num text-gray-400 text-xs">{b.period_key}</span>
                  <span className="text-xs text-gray-500">{{ week: "邀请周榜", month: "邀请月榜", submit_week: "周榜投稿王", submit_month: "月度投稿王" }[b.period_type] || b.period_type}</span>
                  <span className="font-mono-num text-gray-200 flex-1">{b.username}</span>
                  <span className="font-mono-num text-[#D4AF37]">+¥{b.amount.toLocaleString()}</span>
                  <span className="text-xs text-gray-600">{b.invites} 人邀请</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel p-8 mb-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h3 className="font-serif-sc text-lg font-bold flex items-center gap-2">
              <CircleDollarSign className="w-5 h-5 text-[#D4AF37]" /> 提现审核（{withdrawals.filter((w) => w.status === "pending").length} 笔待处理）
            </h3>
            <button data-testid="export-withdrawals-btn" onClick={exportWithdrawals} className="btn-ghost-gold px-4 py-2 text-xs inline-flex items-center gap-2">
              <Download className="w-3.5 h-3.5" /> 导出提现 CSV
            </button>
          </div>
          {withdrawals.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="withdrawals-empty">暂无提现申请</p>
          ) : (
            <div className="max-h-80 overflow-auto border border-[#D4AF37]/10" data-lenis-prevent data-testid="admin-withdrawals-table">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#0F0F14]">
                  <tr className="text-left text-xs text-gray-500 tracking-widest">
                    <th className="px-4 py-3 font-normal">单号</th>
                    <th className="px-4 py-3 font-normal">会员</th>
                    <th className="px-4 py-3 font-normal">金额</th>
                    <th className="px-4 py-3 font-normal">USDT地址</th>
                    <th className="px-4 py-3 font-normal">状态</th>
                    <th className="px-4 py-3 font-normal">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4AF37]/10">
                  {withdrawals.map((w, i) => (
                    <tr key={w.wid}>
                      <td className="px-4 py-2.5 font-mono-num text-gray-400 text-xs">{w.wid}</td>
                      <td className="px-4 py-2.5 font-mono-num text-gray-200">{w.username}</td>
                      <td className="px-4 py-2.5 font-mono-num text-[#F3E5AB]">
                        ¥{w.amount.toLocaleString()}
                        {w.fee_amount > 0 && (
                          <span className="block text-[10px] text-gray-500">手续费 ¥{w.fee_amount} · 实收 ¥{w.net_amount}</span>
                        )}
                        {w.usdt_amount != null && (
                          <span className="block text-[10px] text-[#D4AF37]/80">≈ {w.usdt_amount} USDT · 汇率 {w.rate}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 font-mono-num text-xs text-gray-400" title={w.usdt_address}>
                        {w.usdt_address.slice(0, 8)}...{w.usdt_address.slice(-6)}
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={`text-[11px] tracking-widest px-2 py-0.5 border ${
                            w.status === "pending"
                              ? "text-yellow-400 border-yellow-500/40"
                              : w.status === "paid"
                                ? "text-emerald-400 border-emerald-500/40"
                                : "text-red-400 border-red-500/40"
                          }`}
                        >
                          {w.status === "pending" ? "审核中" : w.status === "paid" ? "已打款" : "已拒绝"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        {w.status === "pending" ? (
                          <span className="flex gap-2">
                            <button
                              data-testid={`withdraw-approve-btn-${i}`}
                              onClick={() => actWithdraw(w.wid, "approve")}
                              className="text-xs text-[#070709] bg-[#D4AF37] px-3 py-1.5 hover:bg-[#E5C158] transition-colors"
                            >
                              确认打款
                            </button>
                            <button
                              data-testid={`withdraw-reject-btn-${i}`}
                              onClick={() => actWithdraw(w.wid, "reject")}
                              className="text-xs text-red-400 border border-red-500/40 px-3 py-1.5 hover:bg-red-500/10 transition-colors"
                            >
                              拒绝
                            </button>
                          </span>
                        ) : (
                          <span className="text-xs text-gray-600 font-mono-num">{(w.processed_at || "").slice(0, 10)}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}

        {tab === "members" && (
        <div className="panel p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-serif-sc text-lg font-bold flex items-center gap-2">
              <Users className="w-5 h-5 text-[#D4AF37]" /> 会员列表（{users.length}）
            </h3>
            <button
              data-testid="export-members-btn"
              onClick={exportMembers}
              className="btn-ghost-gold px-5 py-2 text-xs inline-flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" /> 导出会员 CSV
            </button>
          </div>
          <div className="border border-[#D4AF37]/15 bg-[#0A0A0E] p-4 mb-6" data-testid="verify-member-box">
            <p className="text-xs text-gray-500 tracking-widest mb-3">海鸥群入群核验 · 输入申请时填写的合赢账户，一键查验会员身份</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                data-testid="verify-member-input"
                value={verifyQuery}
                onChange={(e) => setVerifyQuery(e.target.value)}
                placeholder="合赢账户（注册手机号 / 用户名）"
                className="input-gold flex-1 px-4 py-2.5 text-sm font-mono-num"
              />
              <button data-testid="verify-member-btn" onClick={verifyMember} className="btn-gold px-6 py-2.5 text-xs whitespace-nowrap">
                立即核验
              </button>
            </div>
            {verifyResult && (
              <div className="mt-4 border-t border-[#D4AF37]/10 pt-4" data-testid="verify-member-result">
                {!verifyResult.found ? (
                  <p className="text-sm text-red-400">查无此账户 · 该申请人不是平台会员，建议拒绝入群</p>
                ) : (
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                    <span className="font-mono-num text-gray-200">{verifyResult.username}</span>
                    <span className={`text-[11px] tracking-widest px-2.5 py-1 border ${verifyResult.is_activated ? "text-emerald-400 border-emerald-500/40" : "text-red-400 border-red-500/40"}`} data-testid="verify-member-status">
                      {verifyResult.is_activated ? `已激活 · ${verifyResult.level_name} · 可放行` : "未激活会员 · 建议暂缓放行"}
                    </span>
                    <span className="text-xs text-gray-500">卡密 <b className="font-mono-num text-gray-300">{verifyResult.card_key || "—"}</b></span>
                    <span className="text-xs text-gray-500">邀请 {verifyResult.invite_count} 人</span>
                    <span className="text-xs text-gray-500 font-mono-num">{verifyResult.created_at} 注册</span>
                  </div>
                )}
                <div className="flex items-center gap-3 mt-3">
                  {verifyResult.verdict ? (
                    <span
                      data-testid="verify-verdict-badge"
                      className={`text-[11px] tracking-widest px-2.5 py-1 border ${
                        verifyResult.verdict === "approved" ? "text-emerald-400 border-emerald-500/40" : "text-red-400 border-red-500/40"
                      }`}
                    >
                      结论已留痕：{verifyResult.verdict === "approved" ? "已放行" : "已拒绝"}
                    </span>
                  ) : (
                    <>
                      <span className="text-[11px] text-gray-600 tracking-widest">本次结论（自动留痕）：</span>
                      <button
                        data-testid="verify-approve-btn"
                        onClick={() => setVerdict(verifyResult.vid, "approved")}
                        className="text-xs text-[#070709] bg-[#D4AF37] px-4 py-1.5 hover:bg-[#E5C158] transition-colors"
                      >
                        放行入群
                      </button>
                      <button
                        data-testid="verify-reject-btn"
                        onClick={() => setVerdict(verifyResult.vid, "rejected")}
                        className="text-xs text-red-400 border border-red-500/40 px-4 py-1.5 hover:bg-red-500/10 transition-colors"
                      >
                        拒绝入群
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
            {verifyLogs.length > 0 && (
              <div className="mt-5 border-t border-[#D4AF37]/10 pt-4" data-testid="verify-log-list">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] text-gray-600 tracking-widest">核验台账（最近 {Math.min(verifyLogs.length, 10)} 条 · 全程留痕可追溯）</p>
                <button data-testid="export-verify-logs-btn" onClick={exportVerifyLogs} className="text-[10px] text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1 inline-flex items-center gap-1 hover:bg-[#D4AF37]/10 transition-colors">
                  <Download className="w-3 h-3" /> 导出台账 CSV
                </button>
              </div>
                <div className="divide-y divide-[#D4AF37]/10 max-h-56 overflow-y-auto" data-lenis-prevent>
                  {verifyLogs.slice(0, 10).map((l) => (
                    <div key={l.vid} className="py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid={`verify-log-${l.vid}`}>
                      <span className="font-mono-num text-gray-600 w-32 shrink-0">{(l.created_at || "").slice(5, 19).replace("T", " ")}</span>
                      <span className="font-mono-num text-gray-200 flex-1">{l.queried}</span>
                      <span className={`text-[10px] ${l.found ? (l.is_activated ? "text-emerald-400" : "text-yellow-400") : "text-red-400"}`}>
                        {l.found ? (l.is_activated ? l.level_name : "未激活") : "查无此人"}
                      </span>
                      <span
                        className={`text-[10px] tracking-widest px-2 py-0.5 border ${
                          l.verdict === "approved"
                            ? "text-emerald-400 border-emerald-500/40"
                            : l.verdict === "rejected"
                              ? "text-red-400 border-red-500/40"
                              : "text-gray-500 border-gray-700"
                        }`}
                      >
                        {l.verdict === "approved" ? "已放行" : l.verdict === "rejected" ? "已拒绝" : "待结论"}
                      </span>
                      <span className="text-[10px] text-gray-600 font-mono-num">操作:{l.admin}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <input
              data-testid="member-search-input"
              value={memberQuery}
              onChange={(e) => setMemberQuery(e.target.value)}
              placeholder="搜索手机号 / 用户名"
              className="input-gold flex-1 px-4 py-2.5 text-sm font-mono-num"
            />
            <div className="flex gap-2 flex-wrap">
              {[["all", "全部"], ["inactive", "未激活"], ["normal", "普通"], ["vip", "VIP"], ["svip", "超级VIP"]].map(([k, label]) => (
                <button
                  key={k}
                  data-testid={`member-filter-${k}`}
                  onClick={() => setMemberLevel(k)}
                  className={`px-3.5 py-2 text-xs tracking-widest border transition-colors ${
                    memberLevel === k ? "border-[#D4AF37]/60 text-[#D4AF37]" : "border-[#D4AF37]/15 text-gray-500 hover:text-gray-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="max-h-80 overflow-auto border border-[#D4AF37]/10" data-lenis-prevent data-testid="users-table">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#0F0F14]">
                <tr className="text-left text-xs text-gray-500 tracking-widest">
                  <th className="px-4 py-3 font-normal">用户名</th>
                  <th className="px-4 py-3 font-normal">等级</th>
                  <th className="px-4 py-3 font-normal">邀请数</th>
                  <th className="px-4 py-3 font-normal">卡密</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#D4AF37]/10">
                {users
                  .filter((u) => (memberLevel === "all" || u.level === memberLevel) && (!memberQuery || u.username.includes(memberQuery.trim())))
                  .map((u) => (
                  <tr
                    key={u.id}
                    data-testid={`member-row-${u.username}`}
                    onClick={() => openMember(u.username)}
                    className="cursor-pointer hover:bg-[#D4AF37]/5 transition-colors"
                  >
                    <td className="px-4 py-2.5 text-gray-200">{u.username}{u.role === "admin" && <span className="ml-2 text-xs text-red-400">管理员</span>}</td>
                    <td className="px-4 py-2.5 text-[#D4AF37]">{u.role === "admin" ? "—" : u.level_name}</td>
                    <td className="px-4 py-2.5 font-mono-num">{u.invite_count}</td>
                    <td className="px-4 py-2.5 font-mono-num text-gray-400">{u.card_key || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        <Dialog open={detailOpen} onOpenChange={(o) => !o && setDetailOpen(false)}>
          <DialogContent
            className="bg-[#0F0F14] border border-[#D4AF37]/25 text-[#F8F9FA] sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-lenis-prevent
            data-testid="member-detail-dialog"
          >
            <DialogTitle className="font-serif-sc text-xl tracking-widest">会员画像</DialogTitle>
            {!detail ? (
              <p className="text-sm text-gray-500 py-10 text-center">加载中...</p>
            ) : (
              <div className="flex flex-col gap-6 mt-2">
                <div data-testid="detail-profile">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className="font-mono-num text-xl text-[#F3E5AB]">{detail.user.username}</span>
                    <span className="text-[11px] tracking-widest px-2.5 py-1 border border-[#D4AF37]/40 text-[#D4AF37]">
                      {detail.user.level_name}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs text-gray-400">
                    <span>邀请码：<b className="font-mono-num text-gray-200">{detail.user.invite_code}</b></span>
                    <span>卡密：<b className="font-mono-num text-gray-200">{detail.user.card_key || "未激活"}</b></span>
                    <span>邀请人：<b className="font-mono-num text-gray-200">{detail.inviter ? detail.inviter.username : "无（自然注册）"}</b></span>
                    <span>注册时间：<b className="font-mono-num text-gray-200">{(detail.user.created_at || "").slice(0, 19).replace("T", " ")}</b></span>
                    <span className="sm:col-span-2 break-all">USDT：<b className="font-mono-num text-gray-200">{detail.user.usdt_address || "未绑定"}</b></span>
                  </div>
                  <div className="grid grid-cols-3 gap-px bg-[#D4AF37]/10 border border-[#D4AF37]/10 mt-4">
                    {[
                      ["累计奖励", detail.wallet.total_earned],
                      ["可提现", detail.wallet.available],
                      ["审核中/已打款", detail.wallet.held],
                    ].map(([label, v]) => (
                      <div key={label} className="bg-[#0A0A0E] p-3 text-center">
                        <p className="text-[10px] text-gray-500 tracking-widest mb-1">{label}</p>
                        <p className="font-mono-num text-lg text-[#D4AF37]">¥{v.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div data-testid="detail-invites">
                  <h4 className="font-serif-sc font-bold mb-3">邀请链路（直推 {detail.invites.length} 人）</h4>
                  {detail.invites.length === 0 ? (
                    <p className="text-xs text-gray-600">暂无直推成员</p>
                  ) : (
                    <div className="max-h-40 overflow-y-auto divide-y divide-[#D4AF37]/10 border border-[#D4AF37]/10 px-3" data-lenis-prevent>
                      {detail.invites.map((r, i) => (
                        <div key={i} className="py-2 flex justify-between text-xs gap-3">
                          <span className="font-mono-num text-gray-200">{r.invitee_username}</span>
                          <span className="font-mono-num text-[#D4AF37]">+¥{r.reward}</span>
                          <span className="font-mono-num text-gray-600">{(r.created_at || "").slice(0, 10)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div data-testid="detail-bonuses">
                  <h4 className="font-serif-sc font-bold mb-3">榜首奖励（{detail.bonuses.length}）</h4>
                  {detail.bonuses.length === 0 ? (
                    <p className="text-xs text-gray-600">暂无榜首奖励</p>
                  ) : (
                    <div className="divide-y divide-[#D4AF37]/10 border border-[#D4AF37]/10 px-3">
                      {detail.bonuses.map((b, i) => (
                        <div key={i} className="py-2 flex justify-between text-xs gap-3">
                          <span className="text-gray-400">{b.period_key} {b.period_type === "week" ? "周榜" : "月榜"}榜首</span>
                          <span className="font-mono-num text-[#D4AF37]">+¥{b.amount}</span>
                          <span className="font-mono-num text-gray-600">{(b.created_at || "").slice(0, 10)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div data-testid="detail-withdrawals">
                  <h4 className="font-serif-sc font-bold mb-3">提现记录（{detail.withdrawals.length}）</h4>
                  {detail.withdrawals.length === 0 ? (
                    <p className="text-xs text-gray-600">暂无提现记录</p>
                  ) : (
                    <div className="divide-y divide-[#D4AF37]/10 border border-[#D4AF37]/10 px-3">
                      {detail.withdrawals.map((w, i) => (
                        <div key={i} className="py-2 flex items-center gap-3 text-xs">
                          <span className="font-mono-num text-[#F3E5AB]">¥{w.amount}</span>
                          <span className="font-mono-num text-gray-500 flex-1 truncate">{w.usdt_address}</span>
                          <span
                            className={`text-[10px] tracking-widest px-2 py-0.5 border ${
                              w.status === "pending"
                                ? "text-yellow-400 border-yellow-500/40"
                                : w.status === "paid"
                                  ? "text-emerald-400 border-emerald-500/40"
                                  : "text-red-400 border-red-500/40"
                            }`}
                          >
                            {w.status === "pending" ? "审核中" : w.status === "paid" ? "已打款" : "已拒绝"}
                          </span>
                          <span className="font-mono-num text-gray-600">{(w.created_at || "").slice(0, 10)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div data-testid="detail-actions">
                  <h4 className="font-serif-sc font-bold mb-3">快捷操作</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="border border-[#D4AF37]/10 p-4">
                      <p className="text-xs text-gray-500 tracking-widest mb-3">补发通知（客诉/打款说明）</p>
                      <input
                        data-testid="detail-notify-title-input"
                        value={notifyTitle}
                        onChange={(e) => setNotifyTitle(e.target.value)}
                        placeholder="标题（默认：社群通知）"
                        className="input-gold w-full px-3 py-2 text-xs mb-2"
                      />
                      <textarea
                        data-testid="detail-notify-content-input"
                        value={notifyContent}
                        onChange={(e) => setNotifyContent(e.target.value)}
                        placeholder="通知内容，如：您的提现已手动打款，请注意查收"
                        rows={3}
                        className="input-gold w-full px-3 py-2 text-xs mb-3 resize-none"
                      />
                      <button data-testid="detail-notify-send-btn" onClick={sendMemberNotify} className="btn-gold w-full py-2 text-xs">
                        发送通知
                      </button>
                    </div>
                    <div className="border border-[#D4AF37]/10 p-4">
                      <p className="text-xs text-gray-500 tracking-widest mb-3">重置密码（客诉找回）</p>
                      <input
                        data-testid="detail-reset-pwd-input"
                        type="text"
                        value={resetPwd}
                        onChange={(e) => setResetPwd(e.target.value)}
                        placeholder="新密码（至少6位）"
                        className="input-gold w-full px-3 py-2 text-xs mb-3 font-mono-num"
                      />
                      <button data-testid="detail-reset-pwd-btn" onClick={resetMemberPwd} className="btn-ghost-gold w-full py-2 text-xs">
                        确认重置密码
                      </button>
                      <p className="text-[10px] text-gray-600 mt-3 leading-relaxed">
                        重置后系统自动通知该会员，请将新密码通过安全渠道告知本人
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={!!approveTarget} onOpenChange={(o) => !o && setApproveTarget(null)}>
          <DialogContent
            className="bg-[#0F0F14] border border-[#D4AF37]/25 text-[#F8F9FA] sm:max-w-xl max-h-[85vh] overflow-y-auto" data-lenis-prevent
            data-testid="approve-publish-dialog"
          >
            <DialogTitle className="font-serif-sc text-xl tracking-widest">录用并上架「今日项目」</DialogTitle>
            <p className="text-xs text-gray-500 leading-relaxed">
              确认后一步完成：发放录用奖励 → 通知投稿人 → 项目即时上架首页展示。上架字段已按投稿内容预填，可直接确认或微调。
            </p>
            <div className="flex flex-col gap-4 mt-2">
              <label className="block">
                <span className="text-xs text-gray-500 tracking-widest block mb-2">项目标题</span>
                <input
                  data-testid="approve-title-input"
                  value={approveForm.title}
                  onChange={(e) => setApproveForm((f) => ({ ...f, title: e.target.value }))}
                  className="input-gold w-full px-4 py-2.5 text-sm"
                />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs text-gray-500 tracking-widest block mb-2">日赚额度</span>
                  <input
                    data-testid="approve-income-input"
                    value={approveForm.daily_income}
                    onChange={(e) => setApproveForm((f) => ({ ...f, daily_income: e.target.value }))}
                    placeholder="500+"
                    className="input-gold w-full px-4 py-2.5 text-sm font-mono-num"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-gray-500 tracking-widest block mb-2">标签（逗号分隔，最多5个）</span>
                  <input
                    data-testid="approve-tags-input"
                    value={approveForm.tags}
                    onChange={(e) => setApproveForm((f) => ({ ...f, tags: e.target.value }))}
                    placeholder="会员投稿,平台甄选"
                    className="input-gold w-full px-4 py-2.5 text-sm"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-xs text-gray-500 tracking-widest block mb-2">录用奖励（¥，最高 188,888，按项目质量定）</span>
                <input
                  data-testid="approve-reward-input"
                  type="number"
                  min="1"
                  max="188888"
                  value={approveForm.reward}
                  onChange={(e) => setApproveForm((f) => ({ ...f, reward: e.target.value }))}
                  className="input-gold w-full px-4 py-2.5 text-sm font-mono-num"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500 tracking-widest block mb-2">公开简介（访客可见）</span>
                <textarea
                  data-testid="approve-summary-input"
                  value={approveForm.summary}
                  onChange={(e) => setApproveForm((f) => ({ ...f, summary: e.target.value }))}
                  rows={3}
                  className="input-gold w-full px-4 py-2.5 text-sm resize-none"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500 tracking-widest block mb-2">完整拆解（仅激活会员可见）</span>
                <textarea
                  data-testid="approve-detail-input"
                  value={approveForm.detail}
                  onChange={(e) => setApproveForm((f) => ({ ...f, detail: e.target.value }))}
                  rows={5}
                  className="input-gold w-full px-4 py-2.5 text-sm resize-none"
                />
              </label>
              <button data-testid="approve-confirm-btn" onClick={confirmApprove} disabled={busy} className="btn-gold w-full py-3 text-sm">
                {busy ? "处理中..." : "确认录用 · 发奖并上架"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </main>
  );
}
